// src/pages/CookiesPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Cookie, Shield, Eye, BarChart, Settings } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function CookiesPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="cookies-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      <div className="page-hero">
        <div className="container">
          <Cookie size={48} className="hero-icon" />
          <h1>Çerez Politikası</h1>
          <p>Web sitemizde kullanılan çerezler ve veri toplama yöntemleri</p>
          <div className="update-date">Son Güncelleme: 15 Aralık 2024</div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="content-layout">
            {/* Sidebar */}
            <aside className="sidebar">
              <nav className="sticky-nav">
                <h3>İçindekiler</h3>
                <ul>
                  <li><a href="#what-are-cookies">Çerez Nedir?</a></li>
                  <li><a href="#why-we-use">Neden Kullanıyoruz?</a></li>
                  <li><a href="#types">Çerez Türleri</a></li>
                  <li><a href="#our-cookies">Kullandığımız Çerezler</a></li>
                  <li><a href="#third-party">Üçüncü Taraf Çerezler</a></li>
                  <li><a href="#control">Çerez Kontrolü</a></li>
                  <li><a href="#changes">Değişiklikler</a></li>
                  <li><a href="#contact">İletişim</a></li>
                </ul>
              </nav>
            </aside>

            {/* Main Content */}
            <main className="main-content">
              <section id="what-are-cookies" className="content-section">
                <h2>1. Çerez Nedir?</h2>
                <div className="content-box">
                  <p>
                    Çerezler (cookies), bir web sitesini ziyaret ettiğinizde tarayıcınız tarafından 
                    bilgisayarınıza veya mobil cihazınıza kaydedilen küçük metin dosyalarıdır. 
                    Çerezler, web sitesinin düzgün çalışmasını sağlamak ve size daha iyi bir 
                    kullanıcı deneyimi sunmak için kullanılır.
                  </p>
                  <div className="info-card">
                    <Cookie size={32} />
                    <div>
                      <h4>Çerezler Neler İçerir?</h4>
                      <ul>
                        <li>Site adı</li>
                        <li>Benzersiz tanımlayıcı (ID)</li>
                        <li>Geçerlilik süresi</li>
                        <li>Tercih ve ayarlar</li>
                      </ul>
                    </div>
                  </div>
                  <p>
                    Çerezler kişisel verilerinizi (ad, e-posta, vs.) saklamaz. Sadece tarayıcınıza 
                    özgü anonim bilgiler içerir.
                  </p>
                </div>
              </section>

              <section id="why-we-use" className="content-section">
                <h2>2. Çerezleri Neden Kullanıyoruz?</h2>
                <div className="content-box">
                  <p>Çerezleri aşağıdaki amaçlarla kullanıyoruz:</p>
                  <div className="purpose-grid">
                    <div className="purpose-card">
                      <Shield size={28} />
                      <h4>Güvenlik</h4>
                      <p>Hesabınızı korumak ve dolandırıcılığı önlemek</p>
                    </div>
                    <div className="purpose-card">
                      <Settings size={28} />
                      <h4>İşlevsellik</h4>
                      <p>Tercihlerinizi hatırlamak ve siteyi kişiselleştirmek</p>
                    </div>
                    <div className="purpose-card">
                      <BarChart size={28} />
                      <h4>Analitik</h4>
                      <p>Site performansını ölçmek ve iyileştirmek</p>
                    </div>
                    <div className="purpose-card">
                      <Eye size={28} />
                      <h4>Pazarlama</h4>
                      <p>Size uygun reklamlar göstermek</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="types" className="content-section">
                <h2>3. Çerez Türleri</h2>
                <div className="content-box">
                  <h3>3.1. Geçerlilik Süresine Göre</h3>
                  <div className="cookie-types">
                    <div className="type-card">
                      <h4>🕐 Oturum Çerezleri (Session)</h4>
                      <p>
                        Tarayıcıyı kapattığınızda otomatik olarak silinir. Gezinme esnasında 
                        geçici bilgileri saklar.
                      </p>
                    </div>
                    <div className="type-card">
                      <h4>💾 Kalıcı Çerezler (Persistent)</h4>
                      <p>
                        Belirlenen süre boyunca cihazınızda kalır. Tercihlerinizi hatırlamak 
                        için kullanılır (örn: dil seçimi).
                      </p>
                    </div>
                  </div>

                  <h3>3.2. Kaynağına Göre</h3>
                  <div className="cookie-types">
                    <div className="type-card">
                      <h4>🏠 Birinci Taraf Çerezler</h4>
                      <p>
                        Doğrudan sitemiz tarafından oluşturulur. Site işlevselliği için 
                        gereklidir.
                      </p>
                    </div>
                    <div className="type-card">
                      <h4>🌐 Üçüncü Taraf Çerezler</h4>
                      <p>
                        Başka servisler tarafından yerleştirilir (örn: Google Analytics, 
                        Facebook Pixel).
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="our-cookies" className="content-section">
                <h2>4. Kullandığımız Çerezler</h2>
                <div className="content-box">
                  <div className="cookie-category">
                    <div className="category-header essential">
                      <Shield size={24} />
                      <div>
                        <h3>Zorunlu Çerezler</h3>
                        <span className="badge required">Gerekli</span>
                      </div>
                    </div>
                    <p>
                      Bu çerezler sitenin çalışması için zorunludur ve kapatılamaz. 
                      Genellikle giriş yapma, sepet kullanma gibi işlemler için gereklidir.
                    </p>
                    <table className="cookie-table">
                      <thead>
                        <tr>
                          <th>Çerez Adı</th>
                          <th>Amaç</th>
                          <th>Süre</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><code>session_id</code></td>
                          <td>Oturum yönetimi</td>
                          <td>Oturum sonu</td>
                        </tr>
                        <tr>
                          <td><code>auth_token</code></td>
                          <td>Kimlik doğrulama</td>
                          <td>30 gün</td>
                        </tr>
                        <tr>
                          <td><code>cart_items</code></td>
                          <td>Sepet bilgisi</td>
                          <td>7 gün</td>
                        </tr>
                        <tr>
                          <td><code>csrf_token</code></td>
                          <td>Güvenlik</td>
                          <td>Oturum sonu</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="cookie-category">
                    <div className="category-header functional">
                      <Settings size={24} />
                      <div>
                        <h3>İşlevsellik Çerezleri</h3>
                        <span className="badge optional">İsteğe Bağlı</span>
                      </div>
                    </div>
                    <p>
                      Tercihlerinizi hatırlar ve size kişiselleştirilmiş deneyim sunar.
                    </p>
                    <table className="cookie-table">
                      <thead>
                        <tr>
                          <th>Çerez Adı</th>
                          <th>Amaç</th>
                          <th>Süre</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><code>language</code></td>
                          <td>Dil tercihi</td>
                          <td>1 yıl</td>
                        </tr>
                        <tr>
                          <td><code>theme</code></td>
                          <td>Tema tercihi</td>
                          <td>1 yıl</td>
                        </tr>
                        <tr>
                          <td><code>recently_viewed</code></td>
                          <td>Son görüntülenen ürünler</td>
                          <td>30 gün</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="cookie-category">
                    <div className="category-header analytics">
                      <BarChart size={24} />
                      <div>
                        <h3>Performans/Analitik Çerezleri</h3>
                        <span className="badge optional">İsteğe Bağlı</span>
                      </div>
                    </div>
                    <p>
                      Site kullanımını analiz eder ve performansı iyileştirmemize yardımcı olur.
                    </p>
                    <table className="cookie-table">
                      <thead>
                        <tr>
                          <th>Çerez Adı</th>
                          <th>Amaç</th>
                          <th>Süre</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><code>_ga</code></td>
                          <td>Google Analytics - Kullanıcı tanımlama</td>
                          <td>2 yıl</td>
                        </tr>
                        <tr>
                          <td><code>_gid</code></td>
                          <td>Google Analytics - Kullanıcı tanımlama</td>
                          <td>24 saat</td>
                        </tr>
                        <tr>
                          <td><code>_gat</code></td>
                          <td>Google Analytics - İstek hızı sınırlama</td>
                          <td>1 dakika</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="cookie-category">
                    <div className="category-header marketing">
                      <Eye size={24} />
                      <div>
                        <h3>Pazarlama/Hedefleme Çerezleri</h3>
                        <span className="badge optional">İsteğe Bağlı</span>
                      </div>
                    </div>
                    <p>
                      İlgi alanlarınıza göre reklamlar gösterir ve kampanya etkinliğini ölçer.
                    </p>
                    <table className="cookie-table">
                      <thead>
                        <tr>
                          <th>Çerez Adı</th>
                          <th>Amaç</th>
                          <th>Süre</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><code>_fbp</code></td>
                          <td>Facebook Pixel - Dönüşüm takibi</td>
                          <td>3 ay</td>
                        </tr>
                        <tr>
                          <td><code>ads_id</code></td>
                          <td>Reklam kişiselleştirme</td>
                          <td>1 yıl</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <section id="third-party" className="content-section">
                <h2>5. Üçüncü Taraf Çerezler</h2>
                <div className="content-box">
                  <p>
                    Sitemizde aşağıdaki üçüncü taraf servislerin çerezlerini kullanıyoruz:
                  </p>
                  <div className="third-party-list">
                    <div className="third-party-item">
                      <h4>Google Analytics</h4>
                      <p>
                        Site trafiğini analiz etmek için kullanılır. Anonim veriler toplar.
                        <br />
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                          Google Gizlilik Politikası →
                        </a>
                      </p>
                    </div>
                    <div className="third-party-item">
                      <h4>Facebook Pixel</h4>
                      <p>
                        Reklam kampanyalarının etkinliğini ölçmek için kullanılır.
                        <br />
                        <a href="https://www.facebook.com/privacy" target="_blank" rel="noopener noreferrer">
                          Facebook Gizlilik Politikası →
                        </a>
                      </p>
                    </div>
                    <div className="third-party-item">
                      <h4>Ödeme Sağlayıcıları</h4>
                      <p>
                        Güvenli ödeme işlemleri için bankalar ve ödeme sistemleri çerez kullanır.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="control" className="content-section">
                <h2>6. Çerezleri Nasıl Kontrol Edersiniz?</h2>
                <div className="content-box">
                  <h3>6.1. Tarayıcı Ayarları</h3>
                  <p>
                    Çoğu tarayıcı, çerezleri kabul etme veya reddetme konusunda size kontrol sağlar:
                  </p>
                  <div className="browser-links">
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="browser-link">
                      Chrome Çerez Ayarları
                    </a>
                    <a href="https://support.mozilla.org/tr/kb/cerezleri-silme" target="_blank" rel="noopener noreferrer" className="browser-link">
                      Firefox Çerez Ayarları
                    </a>
                    <a href="https://support.apple.com/tr-tr/guide/safari" target="_blank" rel="noopener noreferrer" className="browser-link">
                      Safari Çerez Ayarları
                    </a>
                    <a href="https://support.microsoft.com/tr-tr/microsoft-edge" target="_blank" rel="noopener noreferrer" className="browser-link">
                      Edge Çerez Ayarları
                    </a>
                  </div>

                  <h3>6.2. Çerez Tercihlerinizi Yönetin</h3>
                  <p>
                    Sitemizde çerez tercihlerinizi istediğiniz zaman değiştirebilirsiniz:
                  </p>
                  <button className="manage-cookies-btn" onClick={() => {
                    // Bu butona tıklandığında CookieConsent açılacak
                    localStorage.removeItem('cookieConsent');
                    window.location.reload();
                  }}>
                    🍪 Çerez Ayarlarını Yönet
                  </button>

                  <div className="warning-box">
                    <h4>⚠️ Önemli Uyarı</h4>
                    <p>
                      Çerezleri devre dışı bırakırsanız, sitenin bazı özellikleri düzgün 
                      çalışmayabilir. Özellikle zorunlu çerezleri engellerseniz giriş yapamaz 
                      veya sepet kullanamazsınız.
                    </p>
                  </div>
                </div>
              </section>

              <section id="changes" className="content-section">
                <h2>7. Politika Değişiklikleri</h2>
                <div className="content-box">
                  <p>
                    Bu Çerez Politikası'nı zaman zaman güncelleyebiliriz. Değişiklikler 
                    yapıldığında, bu sayfada yayınlanacak ve "Son Güncelleme" tarihi 
                    güncellenecektir.
                  </p>
                  <p>
                    Önemli değişiklikler için sizi e-posta veya site bildirimi ile 
                    bilgilendireceğiz.
                  </p>
                </div>
              </section>

              <section id="contact" className="content-section">
                <h2>8. İletişim</h2>
                <div className="content-box">
                  <p>
                    Çerez Politikası hakkında sorularınız varsa bizimle iletişime geçin:
                  </p>
                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>E-posta:</strong>
                      <a href="mailto:info@tronapazarlama.com">info@tronapazarlama.com</a>
                    </div>
                    <div className="contact-item">
                      <strong>Gizlilik Politikası:</strong>
                      <a href="/privacy">Gizlilik Politikamızı İnceleyin →</a>
                    </div>
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .cookies-page {
        min-height: 100vh;
        background: #f8f9fa;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      .page-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 80px 0;
        text-align: center;
        color: #fff;
      }

      .hero-icon {
        margin-bottom: 24px;
        opacity: 0.9;
      }

      .page-hero h1 {
        font-size: 48px;
        font-weight: 800;
        margin-bottom: 16px;
      }

      .page-hero p {
        font-size: 18px;
        opacity: 0.8;
        margin-bottom: 16px;
      }

      .update-date {
        display: inline-block;
        background: rgba(255,255,255,0.2);
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
      }

      .page-content {
        padding: 60px 0;
      }

      .content-layout {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 40px;
        align-items: start;
      }

      .sidebar {
        position: relative;
      }

      .sticky-nav {
        position: sticky;
        top: 100px;
        background: #fff;
        padding: 24px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .sticky-nav h3 {
        font-size: 18px;
        margin-bottom: 16px;
        color: #2B2B2B;
      }

      .sticky-nav ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .sticky-nav a {
        display: block;
        padding: 10px 12px;
        color: #495057;
        text-decoration: none;
        border-radius: 8px;
        transition: all 0.2s;
        font-size: 14px;
      }

      .sticky-nav a:hover {
        background: #f8f9fa;
        color: #E5332A;
        transform: translateX(4px);
      }

      .main-content {
        min-width: 0;
      }

      .content-section {
        margin-bottom: 48px;
      }

      .content-section h2 {
        font-size: 32px;
        font-weight: 800;
        margin-bottom: 24px;
        color: #2B2B2B;
        padding-top: 80px;
        margin-top: -80px;
      }

      .content-section h3 {
        font-size: 24px;
        font-weight: 700;
        margin: 32px 0 16px;
        color: #2B2B2B;
      }

      .content-section h4 {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .content-box {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .content-box p {
        color: #495057;
        line-height: 1.8;
        margin-bottom: 16px;
      }

      .content-box a {
        color: #E5332A;
        text-decoration: none;
        font-weight: 600;
      }

      .content-box a:hover {
        text-decoration: underline;
      }

      code {
        background: #f8f9fa;
        padding: 2px 8px;
        border-radius: 4px;
        font-family: monospace;
        font-size: 14px;
        color: #E5332A;
      }

      .info-card {
        display: flex;
        gap: 20px;
        padding: 24px;
        background: #eff6ff;
        border-left: 4px solid #3b82f6;
        border-radius: 8px;
        margin: 24px 0;
      }

      .info-card svg {
        color: #3b82f6;
        flex-shrink: 0;
      }

      .info-card ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 12px;
      }

      .info-card li {
        color: #495057;
        padding-left: 20px;
        position: relative;
      }

      .info-card li::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #3b82f6;
        font-weight: 700;
      }

      .purpose-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        margin: 24px 0;
      }

      .purpose-card {
        padding: 24px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: center;
      }

      .purpose-card svg {
        color: #E5332A;
        margin-bottom: 12px;
      }

      .purpose-card h4 {
        margin-bottom: 8px;
      }

      .purpose-card p {
        margin: 0;
        font-size: 14px;
      }

      .cookie-types {
        display: grid;
        gap: 20px;
        margin: 24px 0;
      }

      .type-card {
        padding: 20px;
        background: #f8f9fa;
        border-left: 4px solid #E5332A;
        border-radius: 8px;
      }

      .type-card h4 {
        margin-bottom: 12px;
      }

      .type-card p {
        margin: 0;
        font-size: 14px;
      }

      .cookie-category {
        margin-bottom: 40px;
      }

      .category-header {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        border-radius: 12px;
        margin-bottom: 16px;
      }

      .category-header.essential {
        background: linear-gradient(135deg, #E5332A, #ff4436);
        color: #fff;
      }

      .category-header.functional {
        background: linear-gradient(135deg, #3b82f6, #2563eb);
        color: #fff;
      }

      .category-header.analytics {
        background: linear-gradient(135deg, #06d6a0, #00b894);
        color: #fff;
      }

      .category-header.marketing {
        background: linear-gradient(135deg, #f59e0b, #d97706);
        color: #fff;
      }

      .category-header > div {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .category-header h3 {
        margin: 0;
        font-size: 20px;
      }

      .badge {
        padding: 4px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
      }

      .badge.required {
        background: rgba(255,255,255,0.3);
      }

      .badge.optional {
        background: rgba(255,255,255,0.3);
      }

      .cookie-table {
        width: 100%;
        border-collapse: collapse;
        margin: 16px 0;
      }

      .cookie-table th {
        background: #f8f9fa;
        padding: 12px;
        text-align: left;
        font-weight: 700;
        border-bottom: 2px solid #e9ecef;
      }

      .cookie-table td {
        padding: 12px;
        border-bottom: 1px solid #e9ecef;
      }

      .cookie-table tr:last-child td {
        border-bottom: none;
      }

      .third-party-list {
        display: grid;
        gap: 20px;
        margin: 24px 0;
      }

      .third-party-item {
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .third-party-item h4 {
        margin-bottom: 12px;
      }

      .third-party-item p {
        margin: 0;
        font-size: 14px;
      }

      .browser-links {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        margin: 24px 0;
      }

      .browser-link {
        padding: 16px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: center;
        color: #E5332A;
        font-weight: 600;
        transition: all 0.3s;
      }

      .browser-link:hover {
        background: #E5332A;
        color: #fff;
        transform: translateY(-4px);
      }

      .manage-cookies-btn {
        padding: 16px 32px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        color: #fff;
        border: none;
        border-radius: 12px;
        font-size: 16px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s;
        margin: 24px 0;
      }

      .manage-cookies-btn:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(229, 51, 42, 0.3);
      }

      .warning-box {
        padding: 20px;
        background: #fffbeb;
        border: 2px solid #f59e0b;
        border-radius: 12px;
        margin: 24px 0;
      }

      .warning-box h4 {
        color: #d97706;
        margin-bottom: 8px;
      }

      .warning-box p {
        margin: 0;
      }

      .contact-info {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin: 24px 0;
      }

      .contact-item {
        padding: 16px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .contact-item strong {
        display: block;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      @media (max-width: 992px) {
        .content-layout {
          grid-template-columns: 1fr;
        }

        .sidebar {
          order: 2;
        }

        .sticky-nav {
          position: static;
        }

        .purpose-grid {
          grid-template-columns: 1fr;
        }

        .browser-links {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 768px) {
        .page-hero h1 {
          font-size: 32px;
        }

        .content-section h2 {
          font-size: 24px;
        }

        .content-box {
          padding: 24px;
        }

        .cookie-table {
          font-size: 14px;
        }

        .cookie-table th,
        .cookie-table td {
          padding: 8px;
        }
      }
    `}</style>
  );
}